import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import type { ReactCropperElement } from "react-cropper";

import Modal from "components/Modal";
import { VISIBILITY_LIST } from "constants/haulpay-broker";
import { useUserFeature } from "hooks/useUserFeature";
import type { IOption } from "models/Form";

import { ImageDetailsStage } from "./imageEditor/ImageDetailsStage";
import { ImageFramingStage } from "./imageEditor/ImageFramingStage";

interface CropModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: {
    id: string;
    name: string;
    preview: string;
    category?: IOption[];
    visibleTo?: string[];
  };
  categories: IOption[];
  onCategoryChange: (id: string, category: IOption[]) => void;
  onCrop: () => Promise<boolean> | boolean;
  cropperRef: RefObject<ReactCropperElement>;
  onVisibilityChange?: (id: string, visibleTo: string[]) => void;
  onUpload?: () => Promise<unknown>;
  isUploading?: boolean;
  uploadProgress?: number;
  isCompressing?: boolean;
  compressionProgress?: number;
  isAssignedLoadsPage?: boolean;
  showVisibility?: boolean;
  disableCategorySelection?: boolean;
  zIndex?: string;
}

const normalizeList = (items: string[]) =>
  [...items]
    .map((item) => item.trim())
    .filter(Boolean)
    .sort();

const findVisibilityOption = (items?: string[]) => {
  if (!items?.length) return undefined;
  const normalized = normalizeList(items);
  return VISIBILITY_LIST.find((option) => {
    const optionValues = normalizeList(String(option.value).split(","));
    return (
      optionValues.length === normalized.length &&
      optionValues.every((value, index) => value === normalized[index])
    );
  });
};

export const CropModal = ({
  isOpen,
  onClose,
  file,
  categories,
  onCategoryChange,
  onCrop,
  cropperRef,
  onVisibilityChange,
  onUpload,
  isUploading = false,
  uploadProgress = 0,
  isCompressing = false,
  compressionProgress = 0,
  isAssignedLoadsPage = false,
  showVisibility = true,
  disableCategorySelection = false,
  zIndex
}: Readonly<CropModalProps>) => {
  const { isCarrier } = useUserFeature();
  const [stage, setStage] = useState<"framing" | "details">("framing");
  const [isConfirming, setIsConfirming] = useState(false);
  const confirmTokenRef = useRef(0);
  const [visibleTo, setVisibleTo] = useState<IOption>();
  const visibilityChangeRef = useRef(onVisibilityChange);

  const canEditVisibility = showVisibility && !isCarrier && !isAssignedLoadsPage;
  const forcedVisibility = useMemo(() => {
    if (!showVisibility) return undefined;
    let optionIndex = 0;
    if (!isCarrier && isAssignedLoadsPage) optionIndex = 2;
    return String(VISIBILITY_LIST[optionIndex]?.value ?? "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }, [isAssignedLoadsPage, isCarrier, showVisibility]);

  useEffect(() => {
    visibilityChangeRef.current = onVisibilityChange;
  }, [onVisibilityChange]);

  useEffect(() => {
    if (isOpen) setStage("framing");
  }, [file.id, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (canEditVisibility) {
      setVisibleTo(findVisibilityOption(file.visibleTo));
    } else {
      setVisibleTo(undefined);
      if (forcedVisibility) visibilityChangeRef.current?.(file.id, forcedVisibility);
    }
  }, [canEditVisibility, file.id, file.visibleTo, forcedVisibility, isOpen]);

  // Closing invalidates any confirm that is mid-flight. The yield below means a confirm no longer
  // completes in the same tick it started, so without this a Cancel during those frames would still
  // run onCrop() afterwards -- and callers like StepThree's UploadSection remove the file in their
  // onClose, so it would crop a file that no longer exists and surface a spurious error.
  const cancelPendingConfirm = () => {
    confirmTokenRef.current += 1;
  };

  const handleClose = () => {
    cancelPendingConfirm();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) cancelPendingConfirm();
  }, [isOpen]);

  // onCrop rasterizes and JPEG-encodes the photo, and getCroppedCanvas is synchronous, so the
  // thread is blocked for the whole thing. Setting isConfirming alone would not show: React cannot
  // paint between the state update and the blocking call in the same tick. The double rAF yields
  // until after the spinner has actually been painted before the work starts.
  const handleConfirmFraming = async () => {
    if (isConfirming) return;
    setIsConfirming(true);
    const token = confirmTokenRef.current;
    try {
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve(null)));
      });
      if (token !== confirmTokenRef.current) return;
      const cropped = await onCrop();
      if (token !== confirmTokenRef.current) return;
      if (cropped) setStage("details");
    } finally {
      setIsConfirming(false);
    }
  };

  const handleVisibilityChange = (option: IOption) => {
    setVisibleTo(option);
    onVisibilityChange?.(file.id, String(option.value).split(","));
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleClose}
      zIndex={zIndex}
      size="xxl"
      wrapperClass="!m-0 !h-full !w-full !max-w-full !p-0 desktop:!m-auto desktop:!h-auto desktop:!w-auto desktop:!p-4"
      dialogClass={`!h-full !max-h-full !w-full !max-w-full !overflow-hidden !rounded-none !border-0 !p-0 desktop:!h-auto desktop:!min-h-[42rem] desktop:!max-h-[92vh] desktop:!max-w-5xl desktop:!rounded-xl ${
        stage === "framing" ? "!bg-black" : "!bg-foreground"
      }`}
      closeClass="!right-4 !top-4 !z-30 !bg-[#2d2d2d]/90 !text-white shadow-lg shadow-black/25 backdrop-blur-sm hover:!bg-[#3a3a3a] desktop:!right-6 desktop:!top-6"
      showCloseButton={stage === "details"}>
      {stage === "framing" ? (
        <ImageFramingStage
          cropperRef={cropperRef}
          file={file}
          isActive={isOpen}
          isConfirming={isConfirming}
          onCancel={handleClose}
          onConfirm={handleConfirmFraming}
        />
      ) : (
        <ImageDetailsStage
          file={file}
          categories={categories}
          visibleTo={visibleTo}
          visibilityOptions={VISIBILITY_LIST}
          canEditVisibility={canEditVisibility}
          disableCategorySelection={disableCategorySelection}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          isCompressing={isCompressing}
          compressionProgress={compressionProgress}
          onCategoryChange={(category) => onCategoryChange(file.id, category)}
          onVisibilityChange={handleVisibilityChange}
          onEdit={() => setStage("framing")}
          onSubmit={onUpload}
        />
      )}
    </Modal>
  );
};
